import { ToggleStage } from '@/shared/components/stage/toggle-stage';
import { useTemplateQuestions } from '../hooks/index';
import { TemplateQuestionButton } from './template-question-button';

import { AskMode } from '@/entities/ask-syncly';
import { cn } from '@/shared/utils/cn';

const TEMPLATE_QUESTIONS_TITLE: Record<AskMode, string> = {
  [AskMode.GENERAL]: 'Looking to understand your audience more closely?',
  [AskMode.CONTENT]: 'Want to understand what content resonates?',
};

interface TemplateQuestionsSectionProps {
  onQuestionClick: (question: string) => void;
  className?: string;
  disabled?: boolean;
  queryId: string;
  workspaceId: string;
  mode: AskMode;
}

function TemplateQuestionsLoadingSkeleton() {
  return Array.from({ length: 3 }).map((_, i) => (
    <div
      key={i}
      className="h-18 w-full animate-pulse rounded-lg border border-zinc-200 bg-white shadow-sm"
    />
  ));
}

function TemplateQuestionsContent({
  onQuestionClick,
  disabled,
  queryId,
  workspaceId,
  mode,
}: {
  onQuestionClick: (question: string) => void;
  disabled?: boolean;
  queryId: string;
  workspaceId: string;
  mode: AskMode;
}) {
  const { templateQuestions, loading } = useTemplateQuestions(workspaceId, queryId, mode);

  return (
    <div className="flex flex-col gap-8">
      <div className="text-center">
        <h2 className="text-2xl leading-8 font-semibold text-zinc-500">
          {TEMPLATE_QUESTIONS_TITLE[mode]}
        </h2>
      </div>
      <ToggleStage
        className="flex flex-col gap-4"
        status={!loading}
        fallback={<TemplateQuestionsLoadingSkeleton />}
      >
        {templateQuestions?.map((template, index) => (
          <TemplateQuestionButton
            key={index}
            question={template}
            onClick={() => onQuestionClick(template)}
            disabled={disabled}
          />
        ))}
      </ToggleStage>
    </div>
  );
}

export function TemplateQuestionsSection({
  onQuestionClick,
  className,
  disabled,
  queryId,
  workspaceId,
  mode,
}: TemplateQuestionsSectionProps) {
  return (
    <div className={cn('w-full', className)}>
      <TemplateQuestionsContent
        onQuestionClick={onQuestionClick}
        disabled={disabled}
        queryId={queryId}
        workspaceId={workspaceId}
        mode={mode}
      />
    </div>
  );
}
